import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['connect-everything-even-the-weird-stuff'];

export default function ConnectEverythingEvenTheWeirdStuffPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['your-daily-freedom-number', 'from-sign-up-to-a-real-fire-plan']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'Connected account sync is important, but a real household plan also needs manual accounts, liabilities, and family context that aggregators often miss.',
          'Per Diem is designed for households with mortgages, stock plans, 529s, HSAs, vehicles, and other details that do not fit neatly into a clean dashboard.',
          'The goal is not perfect data purity. The goal is a planning model that can hold the real shape of the household.',
        ]}
      />

      <ArticleSection title="Real households do not fit into a neat aggregator-only model">
        <ArticleParagraph>
          A lot of finance software quietly assumes that if the product can sync your bank and
          brokerage accounts, it has captured the truth of your financial life. That works up to
          a point, but it falls apart fast for households with more going on than checking,
          savings, and a taxable brokerage account.
        </ArticleParagraph>
        <ArticleParagraph>
          Real households have mortgages. They have manual assets that are still part of net worth.
          They have RSUs or ESPP balances, HSAs, 529 plans, private liabilities, vehicles,
          dependents, and future obligations that matter even if no aggregator can classify them
          elegantly.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileAccounts}
        alt="Per Diem mobile accounts screen with assets and liabilities"
        caption="The accounts view only becomes useful when it can hold more than the balances a sync provider happens to classify cleanly."
        variant="phone"
      />

      <ArticleSection title="Connected data is still the foundation">
        <ArticleParagraph>
          None of that means connected data is optional. Account sync is still the fastest path
          to a living financial model. It pulls in balances, transactions, and investment context
          without asking the household to re-enter every number by hand.
        </ArticleParagraph>
        <ArticleParagraph>
          But connected data is the starting point, not the entire product promise. A planning tool
          should not become less useful the moment the household has something unusual.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileLedger}
        alt="Per Diem mobile ledger screen"
        caption="Connected transactions help the model stay current, but they are only one layer of what the household needs to represent."
        variant="phone"
      />

      <ArticleSection title="The important part is what happens after the sync">
        <ArticleParagraph>
          What distinguishes a more complete planning product is the ability to keep building the
          household model after the linked accounts arrive.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'Manual accounts for balances you still want represented clearly.',
            'First-class liabilities such as mortgages and vehicle loans.',
            'Account types that matter for planning, like HSAs, 529s, and stock-plan balances.',
            'Income context that includes wage deductions and the shape of take-home pay.',
            'Family context, dependents, and future life events that affect the plan even though they are not “accounts.”',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="This matters because planning is not just net worth tracking">
        <ArticleParagraph>
          Net worth is useful, but a household does not make decisions from net worth alone.
          It makes decisions from obligations, future expenses, income structure, and the life it
          expects to support along the way.
        </ArticleParagraph>
        <ArticleParagraph>
          A mortgage is not just a liability line. Kids are not just a note in your head. A 529
          plan is not just another account bucket. These details meaningfully change how a household
          interprets current spending and long-term freedom.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.addPartner}
        alt="Per Diem household collaboration screen"
        caption="A household planning system should be able to represent the people and commitments behind the numbers, not just the balances."
      />

      <ArticleSection title="Why manual support is a feature, not a failure">
        <ArticleParagraph>
          There is a common instinct in fintech to treat manual entry like an embarrassing fallback.
          In reality, manual support is part of respecting how messy actual finances can be.
        </ArticleParagraph>
        <ArticleParagraph>
          If a product can only model the parts of your life that are easy for a data provider to
          sync, it is not really modeling your life. It is modeling the easiest slice of it.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem is more useful when it can take the connected accounts, the manual adjustments,
          and the family context and hold them in one coherent household model.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="The goal is one planning system for the whole household">
        <ArticleParagraph>
          The best version of this product is not a prettier account dashboard. It is a planning
          system that can absorb the strange edges of real life without immediately pushing the
          household back into spreadsheets.
        </ArticleParagraph>
        <ArticleParagraph>
          That is what “connect everything, even the weird stuff” really means. Not that every
          financial detail is effortless, but that the household finally has one place where the
          full picture can exist and still be useful.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
